import React from 'react';

import { Skeleton } from '@mui/material';
import { Spacer } from 'src/components';
import {
  ReminderDetailsFooter,
  ReminderDetailsHeader,
  ReminderDetailsMain,
  useReminderFooterStyles,
  useReminderHeaderStyles,
  useReminderMainStyles,
} from 'src/components/reminders/details';
import { useGetReminder } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(_theme => ({
  wrapper: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    position: 'relative',
  },
}));

interface ReminderDetailsContentProps {
  reminderId: UUID;
}

/**
 * Reminder details content for the global drawer.
 * Used when viewing reminder details from the Reminders page.
 */
export function ReminderDetailsContent({ reminderId }: ReminderDetailsContentProps): JSX.Element {
  const { classes } = useStyles();
  const { classes: mainClasses } = useReminderMainStyles();
  const { classes: headerClasses } = useReminderHeaderStyles();
  const { classes: footerClasses } = useReminderFooterStyles();
  const { loading, reminder } = useGetReminder(reminderId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  if (loading || !reminder)
    return (
      <div className={classes.wrapper}>
        <div className={headerClasses.header}>
          <Skeleton height={24} variant="circular" width={24} />
          <Skeleton variant="text" width={80} />
        </div>
        <div className={mainClasses.main}>
          <div className={mainClasses.inputArea}>
            <Skeleton height={32} variant="rectangular" width="100%" />
            <Skeleton height={100} variant="rectangular" width="100%" />
          </div>
          {Array.from({ length: 5 }).map((_, index) => {
            return (
              <div key={index} className={mainClasses.row}>
                <Skeleton variant="text" width={80} />
              </div>
            );
          })}
        </div>
        <Spacer />
        <div className={footerClasses.footer}>
          <Skeleton height={40} variant="circular" width={40} />
        </div>
      </div>
    );

  return (
    <div className={classes.wrapper}>
      <ReminderDetailsHeader reminder={reminder} />
      <ReminderDetailsMain reminder={reminder} />
      <Spacer />
      <ReminderDetailsFooter reminderId={reminderId} />
    </div>
  );
}
